import {ImageMetadata} from '@wandb/weave/common/types/media';
import {Struct} from '@wandb/weave/common/util/types';

import {RunSignature} from '../../types/run';
import {
  getBoundingBoxes,
  getMasks,
  getSingletonValue,
  mediaFilePath,
  mediaSrc,
  runFileSource,
  WBFile,
} from '../../util/media';
import {
  WANDB_BBOX_CLASS_LABEL_KEY,
  WANDB_DELIMETER,
  WANDB_MASK_CLASS_LABEL_KEY,
} from '../../util/mediaConstants';
import {RunWithHistoryAndMediaWandb} from '../MediaCard';

type ImageInfo = {
  imgSrc: string;
  imgFile: string;
  type: 'single' | 'sprite';
};
type GetImageInfoParams = {
  imgMedia: ImageMetadata | null;
  step: number;
  mediaIndex: number;
  mediaKey: string;
  runSignature: RunSignature;
};

export function getImageInfo({
  imgMedia,
  step,
  mediaIndex,
  mediaKey,
  runSignature,
}: GetImageInfoParams): ImageInfo {
  if (imgMedia?.path) {
    return {
      imgSrc: runFileSource(runSignature, imgMedia.path),
      imgFile: imgMedia.path,
      type: 'single',
    };
  }

  let fileParams: Array<string | number>;
  let type: ImageInfo['type'];
  if (imgMedia?._type === 'images/separated') {
    // This is the new format for a collection images which
    // is a set of individual images, not a sprite
    fileParams = [mediaKey, step, mediaIndex];
    type = 'single';
  } else {
    // Our old multiple image format expected a sprite
    // we use the mediaIndex to determine the pixel offset
    // for the image we want
    fileParams = [mediaKey, step];
    type = 'sprite';
  }

  const format = imgMedia?.format ?? 'jpg';

  return {
    imgSrc: mediaSrc(runSignature, fileParams, 'images', format),
    imgFile: mediaFilePath(fileParams, 'images', format),
    type,
  };
}
export type WBFileData = {
  key: string;
  wbFile: WBFile;
  classLabels: {
    [key: number]: string;
  };
};
type GetWBFileByKeyFn = (
  mediaMetadata: ImageMetadata,
  mediaIndex: number
) => Struct<WBFile>;
type GetWBFileDataParams = {
  currentMediaMetadata: ImageMetadata | null;
  mediaIndex: number;
  mediaKey: string;
  run: RunWithHistoryAndMediaWandb;
};
export function getMaskData(params: GetWBFileDataParams): WBFileData[] {
  return getWBFileData(getMasks, WANDB_MASK_CLASS_LABEL_KEY, params);
}
export function getBBoxData(params: GetWBFileDataParams): WBFileData[] {
  return getWBFileData(getBoundingBoxes, WANDB_BBOX_CLASS_LABEL_KEY, params);
}
function getWBFileData(
  getWBFileByKeyFn: GetWBFileByKeyFn,
  classLabelKey: string,
  {currentMediaMetadata, mediaIndex, mediaKey, run}: GetWBFileDataParams
): WBFileData[] {
  if (currentMediaMetadata == null) {
    return [];
  }

  const wbFileByKey = getWBFileByKeyFn(currentMediaMetadata, mediaIndex);

  return Object.entries(wbFileByKey).map(([key, wbFile]) => ({
    key,
    wbFile,
    classLabels: getSingletonValue(
      run,
      classLabelKey,
      mediaKey + WANDB_DELIMETER + key
    ) as {[key: number]: string},
  }));
}
